import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  Grid,
} from '@mui/material';
import { ArrowBack, LocalShipping, Receipt, Assignment, Description, ReceiptLong } from '@mui/icons-material';
import {
  PageHeader,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  StatusDot,
  StatusTone,
  TableEmptyState,
} from '../../components/common/pageKit';
import { formatIndianNumber } from '../../utils/currency';
import { api } from '../../services/apiClient';
import CreditAvailabilityPanel from '../../components/sales/CreditAvailabilityPanel';

// ---------------------------------------------------------------------------
// Sales Order 360: header + items + a traceability rail (source enquiry /
// contract, deliveries, invoices) plus the two hand-off actions -- Create
// Delivery (dialog -> POST /deliveries/from-sales-order/:id) and Create
// Invoice (link into the existing invoice flow). Mirrors
// PurchaseOrderDetailPage.tsx's traceability rail.
// ---------------------------------------------------------------------------

type SoStatus = 'PENDING' | 'CONFIRMED' | 'SHIPPED' | 'DELIVERED' | 'CANCELLED';

const STATUS_TONE: Record<string, StatusTone> = {
  PENDING: 'neutral',
  CONFIRMED: 'info',
  SHIPPED: 'warning',
  DELIVERED: 'success',
  CANCELLED: 'error',
};

const DELIVERY_STATUS_TONE: Record<string, StatusTone> = {
  DRAFT: 'neutral',
  DISPATCHED: 'info',
  DELIVERED: 'success',
  CANCELLED: 'error',
};

interface SalesOrderItem {
  id: number;
  productId?: number | string;
  product_id?: number | string;
  productName?: string;
  product_name?: string;
  quantity: number | string;
  deliveredQty?: number | string;
  unitPrice?: number | string;
  unit_price?: number | string;
  uom?: string;
  taxAmount?: number | string;
  lineTotal?: number | string;
  line_total?: number | string;
}

interface RelatedStub {
  id: number;
  [key: string]: any;
}

interface SalesOrderDetail {
  id: number;
  orderNumber: string;
  customerId?: number | string;
  customerName?: string;
  salespersonName?: string;
  orderDate: string;
  deliveryDate?: string | null;
  subtotal: number | string;
  taxAmount: number | string;
  totalAmount: number | string;
  status: SoStatus;
  enquiryId?: number | string | null;
  contractId?: number | string | null;
  items: SalesOrderItem[];
  related: {
    enquiry: RelatedStub | null;
    contract: RelatedStub | null;
    deliveries: RelatedStub[];
    invoices: RelatedStub[];
  };
}

interface Warehouse {
  id: number | string;
  warehouseCode?: string;
  warehouseName: string;
}

const TRANSPORT_MODES = ['ROAD', 'RAIL', 'AIR', 'SHIP'] as const;

const toNum = (v: number | string | null | undefined): number => {
  if (v === null || v === undefined || v === '') return 0;
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) ? n : 0;
};

const fmtDate = (v: string | null | undefined) => (v ? new Date(v).toLocaleDateString('en-IN') : '—');

const SalesOrderDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [order, setOrder] = useState<SalesOrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [deliveryDialogOpen, setDeliveryDialogOpen] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const { salesOrder } = await api.get<{ salesOrder: SalesOrderDetail }>(`/sales/orders/${id}`);
      setOrder(salesOrder);
    } catch (err: any) {
      setError(err.message || 'Failed to load sales order');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !order) {
    return (
      <Box>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/sales/orders')} sx={{ mb: 2 }}>
          Back to Sales Orders
        </Button>
        <Alert severity="error">{error || 'Sales order not found'}</Alert>
      </Box>
    );
  }

  const canCreateDelivery = order.status !== 'CANCELLED' && order.status !== 'DELIVERED';

  return (
    <Box>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/sales/orders')} sx={{ mb: 1 }}>
        Back to Sales Orders
      </Button>

      <PageHeader
        title={order.orderNumber}
        qualifier={order.customerName ? `· ${order.customerName}` : undefined}
        subtitle={`Raised ${fmtDate(order.orderDate)}${order.salespersonName ? ` by ${order.salespersonName}` : ''}`}
        actions={<StatusDot label={order.status} tone={STATUS_TONE[order.status] || 'neutral'} />}
      />

      {/* Hand-off actions */}
      <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
          Actions
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
          {canCreateDelivery && (
            <Button variant="contained" startIcon={<LocalShipping />} onClick={() => setDeliveryDialogOpen(true)}>
              Create Delivery
            </Button>
          )}
          <Button variant="outlined" startIcon={<Receipt />} onClick={() => navigate(`/sales/invoices/new?salesOrderId=${order.id}`)}>
            Create Invoice
          </Button>
          {!canCreateDelivery && (
            <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
              This order is {order.status.toLowerCase()} -- no further deliveries can be created.
            </Typography>
          )}
        </Box>
      </Paper>

      {/* Credit control + stock availability (real data) */}
      <CreditAvailabilityPanel
        salesOrderId={order.id}
        customerId={order.customerId}
        orderAmount={toNum(order.totalAmount)}
      />

      <Grid container spacing={2.5}>
        {/* Header details */}
        <Grid size={{ xs: 12, md: 7 }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
              Sales Order Details
            </Typography>
            <Grid container spacing={1.5}>
              {[
                ['Customer', order.customerName],
                ['Salesperson', order.salespersonName],
                ['Order Date', fmtDate(order.orderDate)],
                ['Delivery Date', fmtDate(order.deliveryDate)],
              ].map(([label, value]) => (
                <Grid size={{ xs: 6, sm: 4 }} key={label}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {label}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {value || '—'}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>

        {/* Totals */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
              Totals
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">Subtotal</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatIndianNumber(toNum(order.subtotal))}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">Tax</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatIndianNumber(toNum(order.taxAmount))}</Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Total</Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{formatIndianNumber(toNum(order.totalAmount))}</Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Items */}
        <Grid size={{ xs: 12 }}>
          <TableSurface>
            <Box sx={{ p: 2.5, pb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Line Items
              </Typography>
            </Box>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['Product', 'UOM', 'Qty', 'Unit Price', 'Tax', 'Line Total'].map((h, i) => (
                      <TableCell key={h} align={i >= 2 ? 'right' : 'left'} sx={headerCellSx}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {order.items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ border: 0 }}>
                        <TableEmptyState title="No line items on this sales order" />
                      </TableCell>
                    </TableRow>
                  ) : (
                    order.items.map((item) => (
                      <TableRow key={item.id} sx={bodyRowSx}>
                        <TableCell sx={{ py: 1.25 }}>{item.productName || item.product_name || '—'}</TableCell>
                        <TableCell>{item.uom || '—'}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {toNum(item.quantity)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {formatIndianNumber(toNum(item.unitPrice ?? item.unit_price))}
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {formatIndianNumber(toNum(item.taxAmount))}
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                          {formatIndianNumber(toNum(item.lineTotal ?? item.line_total))}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TableSurface>
        </Grid>

        {/* Traceability rail */}
        <Grid size={{ xs: 12 }}>
          <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
            <Box sx={{ p: 2.5, pb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Traceability
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Enquiry / Contract → Sales Order → Delivery → Invoice
              </Typography>
            </Box>

            <Grid container>
              <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={{ px: 2.5, pb: 2 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                  <Description fontSize="inherit" /> Source Enquiry
                </Typography>
                {order.related.enquiry ? (
                  <Box sx={{ cursor: 'pointer' }} onClick={() => navigate(`/sales/enquiries/${order.related.enquiry!.id}`)}>
                    <Typography variant="body2" color="primary" sx={{ fontWeight: 600 }}>
                      {order.related.enquiry.enquiryNumber || order.related.enquiry.enquiry_number || `#${order.related.enquiry.id}`}
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.disabled">Not raised from an enquiry.</Typography>
                )}
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={{ px: 2.5, pb: 2 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                  <Assignment fontSize="inherit" /> Source Contract
                </Typography>
                {order.related.contract ? (
                  <Box sx={{ cursor: 'pointer' }} onClick={() => navigate(`/sales/contracts/${order.related.contract!.id}`)}>
                    <Typography variant="body2" color="primary" sx={{ fontWeight: 600 }}>
                      {order.related.contract.contractNumber || order.related.contract.contract_number || `#${order.related.contract.id}`}
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.disabled">Not raised from a contract.</Typography>
                )}
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={{ px: 2.5, pb: 2 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                  <LocalShipping fontSize="inherit" /> Deliveries
                </Typography>
                {order.related.deliveries.length === 0 ? (
                  <Typography variant="body2" color="text.disabled">No deliveries created yet.</Typography>
                ) : (
                  order.related.deliveries.map((d) => (
                    <Box
                      key={d.id}
                      sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5, cursor: 'pointer', '&:hover': { opacity: 0.75 } }}
                      onClick={() => navigate(`/sales/deliveries/${d.id}`)}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {d.deliveryNumber || d.delivery_number || `#${d.id}`}
                      </Typography>
                      <StatusDot label={(d.status || '').replace(/_/g, ' ')} tone={DELIVERY_STATUS_TONE[d.status] || 'neutral'} />
                    </Box>
                  ))
                )}
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={{ px: 2.5, pb: 2 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                  <ReceiptLong fontSize="inherit" /> Invoices
                </Typography>
                {order.related.invoices.length === 0 ? (
                  <Typography variant="body2" color="text.disabled">No invoices raised yet.</Typography>
                ) : (
                  order.related.invoices.map((inv) => (
                    <Box
                      key={inv.id}
                      sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5, cursor: 'pointer', '&:hover': { opacity: 0.75 } }}
                      onClick={() => navigate('/sales/invoices')}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {inv.invoiceNumber || inv.invoice_no || `#${inv.id}`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatIndianNumber(toNum(inv.totalAmount ?? inv.net_amount))}
                      </Typography>
                    </Box>
                  ))
                )}
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      <CreateDeliveryDialog
        open={deliveryDialogOpen}
        order={order}
        onClose={() => setDeliveryDialogOpen(false)}
        onCreated={(deliveryId) => {
          setDeliveryDialogOpen(false);
          navigate(`/sales/deliveries/${deliveryId}`);
        }}
      />
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Create Delivery dialog
// ---------------------------------------------------------------------------

interface DeliveryLineDraft {
  salesOrderItemId: number;
  productLabel: string;
  orderedQty: number;
  deliveredQty: string;
}

const CreateDeliveryDialog: React.FC<{
  open: boolean;
  order: SalesOrderDetail;
  onClose: () => void;
  onCreated: (deliveryId: number) => void;
}> = ({ open, order, onClose, onCreated }) => {
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [lines, setLines] = useState<DeliveryLineDraft[]>([]);
  const [transporter, setTransporter] = useState('');
  const [vehicleNo, setVehicleNo] = useState('');
  const [transportMode, setTransportMode] = useState<typeof TRANSPORT_MODES[number]>('ROAD');
  const [dispatchFrom, setDispatchFrom] = useState('');
  const [deliverTo, setDeliverTo] = useState('');
  const [distanceKm, setDistanceKm] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [remarks, setRemarks] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setTransporter('');
    setVehicleNo('');
    setTransportMode('ROAD');
    setDispatchFrom('');
    setDeliverTo('');
    setDistanceKm('');
    setWarehouseId('');
    setRemarks('');
    setError(null);
    setLines(
      order.items.map((it) => {
        const ordered = toNum(it.quantity);
        const already = toNum(it.deliveredQty);
        const remaining = Math.max(ordered - already, 0);
        return {
          salesOrderItemId: it.id,
          productLabel: it.productName || it.product_name || `Item ${it.id}`,
          orderedQty: ordered,
          deliveredQty: String(remaining),
        };
      })
    );
    api
      .get<{ warehouses: Warehouse[] }>('/inventory/warehouses')
      .then((r) => setWarehouses(r.warehouses || []))
      .catch(() => setWarehouses([]));
  }, [open, order]);

  const updateLine = (salesOrderItemId: number, deliveredQty: string) =>
    setLines((prev) => prev.map((l) => (l.salesOrderItemId === salesOrderItemId ? { ...l, deliveredQty } : l)));

  const handleSubmit = async () => {
    setError(null);
    const itemsToDeliver = lines.filter((l) => toNum(l.deliveredQty) > 0);
    if (itemsToDeliver.length === 0) {
      setError('Enter a quantity greater than zero for at least one line.');
      return;
    }

    setSubmitting(true);
    try {
      const res: any = await api.post(`/deliveries/from-sales-order/${order.id}`, {
        items: itemsToDeliver.map((l) => ({ salesOrderItemId: l.salesOrderItemId, deliveredQty: toNum(l.deliveredQty) })),
        transporter: transporter.trim() || undefined,
        vehicleNo: vehicleNo.trim() || undefined,
        transportMode,
        dispatchFrom: dispatchFrom.trim() || undefined,
        deliverTo: deliverTo.trim() || undefined,
        distanceKm: distanceKm ? toNum(distanceKm) : undefined,
        warehouseId: warehouseId ? Number(warehouseId) : undefined,
        remarks: remarks.trim() || undefined,
      });
      const deliveryId = res?.delivery?.id ?? res?.id;
      if (deliveryId) {
        onCreated(deliveryId);
      } else {
        onClose();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create delivery');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <LocalShipping color="primary" />
        Create Delivery
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
          Lines to Deliver
        </Typography>
        <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflowX: 'auto', mb: 2.5 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx}>Item</TableCell>
                <TableCell sx={headerCellSx} align="right">Ordered Qty</TableCell>
                <TableCell sx={headerCellSx} align="right">Deliver Qty</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.map((l) => (
                <TableRow key={l.salesOrderItemId} sx={bodyRowSx}>
                  <TableCell sx={{ py: 1 }}>{l.productLabel}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{l.orderedQty}</TableCell>
                  <TableCell align="right" sx={{ minWidth: 100 }}>
                    <TextField
                      size="small"
                      type="number"
                      value={l.deliveredQty}
                      onChange={(e) => updateLine(l.salesOrderItemId, e.target.value)}
                      inputProps={{ min: 0, max: l.orderedQty, step: '0.01' }}
                      sx={{ minWidth: 100 }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
          Dispatch Details
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField fullWidth size="small" label="Transporter" value={transporter} onChange={(e) => setTransporter(e.target.value)} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField fullWidth size="small" label="Vehicle No" value={vehicleNo} onChange={(e) => setVehicleNo(e.target.value)} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Transport Mode"
              value={transportMode}
              onChange={(e) => setTransportMode(e.target.value as typeof TRANSPORT_MODES[number])}
            >
              {TRANSPORT_MODES.map((m) => (
                <MenuItem key={m} value={m}>{m}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField fullWidth size="small" label="Dispatch From" value={dispatchFrom} onChange={(e) => setDispatchFrom(e.target.value)} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField fullWidth size="small" label="Deliver To" value={deliverTo} onChange={(e) => setDeliverTo(e.target.value)} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Distance (km)"
              value={distanceKm}
              onChange={(e) => setDistanceKm(e.target.value)}
              inputProps={{ min: 0 }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Warehouse"
              value={warehouseId}
              onChange={(e) => setWarehouseId(e.target.value)}
            >
              <MenuItem value="">Not assigned</MenuItem>
              {warehouses.map((w) => (
                <MenuItem key={w.id} value={String(w.id)}>
                  {w.warehouseCode ? `${w.warehouseCode} -- ${w.warehouseName}` : w.warehouseName}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField fullWidth size="small" multiline minRows={2} label="Remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 2.5 }}>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
          {submitting ? 'Creating...' : 'Create Delivery'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SalesOrderDetailPage;
